import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import EventForm from '../../components/admin/EventForm';
import AdminLayout from '../../components/admin/AdminLayout';
import { API_URL } from '../../config';
import { Alert, Button, Badge, EmptyState, EmptyStateIcon, EmptyStateTitle, EmptyStateText } from '../../styles/AdminStyles';

export default function EventsPage() {
  const [branches, setBranches] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await fetch(`${API_URL}/home-content/latest`);
        if (!res.ok) throw new Error('Failed to fetch branches');
        const data = await res.json();
        setBranches(data.branches || []);
      } catch (error) {
        console.error('Error fetching branches:', error);
        setError('Failed to load branches');
      }
    };

    fetchBranches();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(`${API_URL}/events`);
        if (!res.ok) throw new Error('Failed to fetch events');
        const data = await res.json();
        setEvents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('EventsPage: Error fetching events:', error);
        setError('Failed to load events');
      }
    };

    fetchEvents();
  }, []);

  const handleSubmit = async (eventData) => {
    setIsLoading(true);
    setError(null);

    try {
      const method = editingEvent ? 'PUT' : 'POST';
      const url = editingEvent 
        ? `${API_URL}/events/${editingEvent._id}`
        : `${API_URL}/events`;

      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save event');
      }

      const savedEvent = await response.json();

      setEvents(prev => {
        if (editingEvent) {
          return prev.map(event => 
            event._id === editingEvent._id ? savedEvent : event
          );
        }
        return [...prev, savedEvent];
      });

      setShowEventForm(false);
      setEditingEvent(null);
      setSuccess('Event saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving event:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (event) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/events/${event._id}`, {
          method: 'DELETE'
        });

        if (!response.ok) throw new Error('Failed to delete event');

        setEvents(prev => prev.filter(e => e._id !== event._id));
        setSuccess('Event deleted successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error('Error deleting event:', error);
        setError('Failed to delete event');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Simple filtering logic using branch IDs directly
  const filteredEvents = Array.isArray(events)
    ? (selectedBranch === 'all'
        ? events
        : events.filter(event => {
            // Handle events with branch "all" - they should show in all filters
            if (event.branch === 'all') return true;
            
            // Handle null/undefined branch
            if (!event.branch) return false;
            
            // Direct comparison with branch ID
            return event.branch === selectedBranch;
          }))
    : [];

  // Helper function to get display name for branch
  const getBranchDisplayName = (branchValue) => {
    if (branchValue === 'all') return 'All Branches';
    if (!branchValue) return 'No Branch';
    
    // Find branch by ID
    const branch = branches.find(b => b._id === branchValue);
    if (branch) return branch.name;
    
    // Fallback for old string identifiers - try to find by name pattern
    if (branchValue === 'accra') {
      const accraBranch = branches.find(b => b.name.includes('Accra'));
      return accraBranch ? accraBranch.name : 'Accra';
    }
    if (branchValue === 'kumasi') {
      const kumasiBranch = branches.find(b => b.name.includes('Headquarters'));
      return kumasiBranch ? kumasiBranch.name : 'Kumasi';
    }
    if (branchValue === 'knust') {
      const knustBranch = branches.find(b => b.name.includes('KNUST'));
      return knustBranch ? knustBranch.name : 'KNUST';
    }
    if (branchValue === 'sunyani') {
      const sunyaniBranch = branches.find(b => b.name.includes('Sunyani'));
      return sunyaniBranch ? sunyaniBranch.name : 'Sunyani';
    }
    
    // Fallback to displaying the value as-is
    return branchValue;
  };

  return (
    <AdminLayout 
      title="Events Management" 
      subtitle="Create and manage events for your branches"
    >
      {error && (
        <Alert $type="error">
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert $type="success">
          {success}
        </Alert>
      )}

      <FilterSection>
        <FilterGroup>
          <FilterLabel>Filter by Branch:</FilterLabel>
          <FilterSelect
            value={selectedBranch}
            onChange={e => setSelectedBranch(e.target.value)}
          >
            <option value="all">All Branches</option>
            {branches.map(branch => {
              const branchId = branch._id;
              return (
                <option key={branch._id || branch.id || branch.name} value={branchId}>
                  {branch.name}
                </option>
              );
            })}
          </FilterSelect>
        </FilterGroup>
        <AddButton
          onClick={() => {
            setEditingEvent(null);
            setShowEventForm(true);
          }}
          disabled={isLoading}
        >
          <span>+</span> Add Event
        </AddButton>
      </FilterSection>

      <AnimatePresence>
        {showEventForm && (
          <EventForm
            event={editingEvent}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowEventForm(false);
              setEditingEvent(null);
            }}
            branches={branches}
          />
        )}
      </AnimatePresence>

      {filteredEvents.length === 0 ? (
        <EmptyStateStyled
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <EmptyStateIcon>📅</EmptyStateIcon>
          <EmptyStateTitle>No events found</EmptyStateTitle>
          <EmptyDescription>
            {selectedBranch === 'all' 
              ? "You haven't created any events yet. Start by adding your first event!"
              : `No events found for ${selectedBranch} branch. Create an event for this branch.`
            }
          </EmptyDescription>
          <EmptyButton
            onClick={() => {
              setEditingEvent(null);
              setShowEventForm(true);
            }}
          >
            <span>+</span> Create Your First Event
          </EmptyButton>
        </EmptyStateStyled>
      ) : (
        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Event</TableHeaderCell>
                <TableHeaderCell>Date & Time</TableHeaderCell>
                <TableHeaderCell>Location</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Branch</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.map((event, index) => (
                <motion.tr
                  key={event._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <TableCell>
                    <EventTitle>{event.summary}</EventTitle>
                    <EventDescription>{event.description}</EventDescription>
                  </TableCell>
                  <TableCell>
                    <EventDate>
                      {new Date(event.start.dateTime).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </EventDate>
                    <EventTime>
                      {new Date(event.start.dateTime).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })} - {new Date(event.end.dateTime).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </EventTime>
                  </TableCell>
                  <TableCell>
                    <LocationText>{event.location}</LocationText>
                  </TableCell>
                  <TableCell>
                    <TypeBadge type={event.type}>
                      {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                    </TypeBadge>
                  </TableCell>
                  <TableCell>
                    <BranchText>{getBranchDisplayName(event.branch)}</BranchText>
                  </TableCell>
                  <TableCell>
                    <ActionButtons>
                      <ActionButton
                        onClick={() => {
                          setEditingEvent(event);
                          setShowEventForm(true);
                        }}
                        $edit
                      >
                        Edit
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleDelete(event)}
                        $delete
                      >
                        Delete
                      </ActionButton>
                    </ActionButtons>
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AdminLayout>
  );
}

const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FilterLabel = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  min-width: 160px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  span {
    font-size: 1.25rem;
    font-weight: 300;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #f3f4f6;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  
  &:hover {
    background-color: #f9fafb;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  border-bottom: 2px solid #e5e7eb;
`;

const TableBody = styled.tbody`
  & tr {
    border-bottom: 1px solid #e5e7eb;
    transition: background-color 0.2s ease;
  }

  & tr:hover {
    background-color: #f9fafb;
  }

  & tr:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem 0.75rem;
  vertical-align: top;
`;

const EventTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
`;

const EventDescription = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EventDate = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const EventTime = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const LocationText = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const TypeBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${props => {
    switch(props.type) {
      case 'special':
        return `
          background-color: #fef3c7;
          color: #92400e;
        `;
      case 'regular':
        return `
          background-color: #d1fae5;
          color: #065f46;
        `;
      case 'outreach':
        return `
          background-color: #ddd6fe;
          color: #5b21b6;
        `;
      default:
        return `
          background-color: #f3f4f6;
          color: #374151;
        `;
    }
  }}
`;

const BranchText = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${props => props.$edit && `
    background-color: #dbeafe;
    color: #1e40af;
    
    &:hover {
      background-color: #bfdbfe;
      transform: translateY(-1px);
    }
  `}

  ${props => props.$delete && `
    background-color: #fee2e2;
    color: #dc2626;
    
    &:hover {
      background-color: #fecaca;
      transform: translateY(-1px);
    }
  `}
`;

const EmptyStateStyled = styled(motion.div)`
  grid-column: 1 / -1;
  text-align: center;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 1rem;
  border: 2px dashed #cbd5e1;
`;

const EmptyDescription = styled.p`
  color: #6b7280;
  font-size: 1rem;
  margin-bottom: 2rem;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const EmptyButton = styled.button`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  span {
    font-size: 1.25rem;
    font-weight: 300;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.1);
  }
`;